/**
 * @file Loads and holds the investment, its account and its project templates for the drawer.
 */
import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";

import { runRequest, useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { DOCUMENT_NOT_FOUND_ERROR } from "@api";
import {
  readAccountOperation,
  readInvestmentOperation as readOperation,
} from "@api/services/investments";
import { useTemplatesStore } from "@components/Templates";

import updateProfileNames from "./helpers/updateProfileNames";
import { processInvestments } from "../../InvestmentForm/InvestmentForm";
import type {
  Account,
  Investment,
  OutletContext,
  TemplatesStore,
  ReadInvestment,
  UpdateInvestment,
} from "../types";

type InvestmentOutput = {
  data: Investment;
};

type AccountOutput = {
  data: Account;
};

export type InvestmentStore = {
  account?: Account;
  isLoading: boolean;
  investment?: Investment;
  setInvestment: (investment?: Investment) => void;
  readInvestment: ReadInvestment;
  updateInvestment: UpdateInvestment;
  projectTemplatesStore: TemplatesStore;
};

/**
 * Loads an investment and its account by ID and exposes them along with the project's templates store.
 */
const useInvestmentStore = (onInvestmentNotFound: (id: string) => void): InvestmentStore => {
  const { request } = useAppContext() as AppContext;
  const {
    projectTemplatesStore: outletProjectTemplatesStore,
  } = useOutletContext<OutletContext>();

  const [ account, setAccount ] = useState<Account | undefined>();
  const [ investment, setInvestment ] = useState<Investment | undefined>();

  const { projectId } = investment || {};

  const templatesStore: TemplatesStore = useTemplatesStore(projectId);
  const { indexTemplates } = templatesStore;

  const indexProjectTemplates = useCallback(() => {
    if (outletProjectTemplatesStore) {
      return;
    }

    return indexTemplates();
  }, [
    indexTemplates,
    outletProjectTemplatesStore,
  ]);

  const isLoading = !investment || !account;

  const readInvestment = useCallback((id: string) => {
    request<InvestmentOutput>(readOperation, { id }, [ DOCUMENT_NOT_FOUND_ERROR ])
      .then(({ data }) => {
        const [ processedInvestment ] = processInvestments([ data ]);
        updateProfileNames(processedInvestment);

        setInvestment(processedInvestment);

        const { investorAccountId } = data;
        return investorAccountId;
      })
      .then((investorAccountId) => request<AccountOutput>(readAccountOperation, { id: investorAccountId }))
      .then(({ data }) => setAccount(data))
      .catch(() => onInvestmentNotFound(id));
  }, [ request, onInvestmentNotFound ]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    return runRequest(() => {
      indexProjectTemplates();
    });
  }, [
    projectId,
    indexProjectTemplates,
  ]);

  /**
   * Merges the given fields into the current investment.
   */
  const updateInvestment = (newInvestment: Partial<Investment>) =>
    setInvestment({ ...investment, ...newInvestment });

  const projectTemplatesStore = outletProjectTemplatesStore
    ? outletProjectTemplatesStore
    : templatesStore;

  return {
    account,
    isLoading,
    investment,
    setInvestment,
    readInvestment,
    updateInvestment,
    projectTemplatesStore,
  };
};

export default useInvestmentStore;
